import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { logger } from '@/lib/logger';

/**
 * Custom hook for managing state with automatic persistence to storage
 * Works cross-platform (web localStorage, mobile AsyncStorage)
 *
 * @param key - Storage key for persistence
 * @param defaultValue - Default value if nothing in storage
 * @returns [value, setValue, isLoaded, hadStoredValue] - hadStoredValue is true when a value was loaded from storage
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, (value: T) => Promise<void>, boolean, boolean] {
  const [value, setValue] = useState<T>(defaultValue);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hadStoredValue, setHadStoredValue] = useState(false);
  // Whether a legacy raw (non-JSON) stored string is acceptable as a value.
  const acceptsRawStringRef = useRef(typeof defaultValue === 'string');

  // Load from storage on mount
  useEffect(() => {
    const loadFromStorage = async () => {
      try {
        let stored: string | null = null;

        if (Platform.OS === 'web') {
          if (typeof window !== 'undefined' && window.localStorage) {
            stored = localStorage.getItem(key);
          }
        } else {
          stored = await AsyncStorage.getItem(key);
        }

        if (stored !== null) {
          let parsed: T;
          try {
            parsed = JSON.parse(stored) as T;
          } catch {
            // Values written before this hook JSON-encoded them (e.g. the raw
            // string "by_title" under collection_sort_order) are still valid
            // when the state is a string; anything else falls back to default.
            if (!acceptsRawStringRef.current) return;
            parsed = stored as unknown as T;
          }
          setValue(parsed);
          setHadStoredValue(true);
        }
      } catch (error) {
        logger.error(`Failed to load ${key} from storage:`, error);
      } finally {
        setIsLoaded(true);
      }
    };

    loadFromStorage();
  }, [key]);

  // Save to storage
  const setPersistedValue = useCallback(
    async (newValue: T) => {
      try {
        const stringValue = JSON.stringify(newValue);

        if (Platform.OS === 'web') {
          if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem(key, stringValue);
          }
        } else {
          await AsyncStorage.setItem(key, stringValue);
        }

        setValue(newValue);
      } catch (error) {
        logger.error(`Failed to save ${key} to storage:`, error);
      }
    },
    [key]
  );

  return [value, setPersistedValue, isLoaded, hadStoredValue];
}

